import { Moment } from "moment";

export const calculateDuration = (fromDate: Date, toDate: Date) => {
  const timeDiff = new Date(toDate).getTime() - new Date(fromDate).getTime();

  const totalMinutes = Math.floor(timeDiff / (60 * 1000));
  const days = Math.floor(totalMinutes / (24 * 60));
  const remainingMinutesAfterDays = totalMinutes % (24 * 60);
  const hours = Math.floor(remainingMinutesAfterDays / 60);
  const minutes = remainingMinutesAfterDays % 60;

  return { days, hours, minutes };
};

export const formatDate = (date: Moment | null): string => {
  if (!date) {
    return "";
  }
  return date.format("MMM D");
};

/**
 * Does a `YYYY-MM-DD` string denote a day that actually exists?
 *
 * The shape is not the question — `2026-02-30` and `2026-99-99` both match
 * `/^\d{4}-\d{2}-\d{2}$/` and neither is a day. JavaScript will not tell you
 * either: `new Date("2026-02-30")` silently rolls over to 2 March rather than
 * throwing, so the only reliable check is to build the date and see whether
 * the components survive the round trip.
 *
 * This is the single definition of "is this a real calendar date?" for the
 * booking URL layers — the two `isValidDate` validators both defer to it
 * rather than re-deciding it.
 */
export const isRealCalendarDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const utc = new Date(Date.UTC(year, month - 1, day));

  return (
    utc.getUTCFullYear() === year &&
    utc.getUTCMonth() === month - 1 &&
    utc.getUTCDate() === day
  );
};
